import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Box, Button, Dialog, DialogContent, Divider, Grid, IconButton, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

const CLOUDFRONT_LOGOS = {
    vehicleIcon: 'https://dns82ajnfncm5.cloudfront.net/logos/EMAIL_VEHICLEICON.png',
    serviceIcon: 'https://dns82ajnfncm5.cloudfront.net/logos/EMAIL_SERVICESICON.png',
    appBtn: 'https://dns82ajnfncm5.cloudfront.net/logos/EMAIL_APPBTN.png',
    googleBtn: 'https://dns82ajnfncm5.cloudfront.net/logos/EMAIL_GOOGLEBTN.png',
    bgmFrame: 'https://dns82ajnfncm5.cloudfront.net/logos/EMAIL_BGFRAME.png',
    apexAuto: 'https://dns82ajnfncm5.cloudfront.net/logos/EMAIL_APEX_AUTO.png',
    emailDropOff: 'https://dns82ajnfncm5.cloudfront.net/logos/EMAIL_DROP_OFF.png',
    emailOldDropOff: 'https://dns82ajnfncm5.cloudfront.net/logos/EMAIL_OLD_DROPOFF.png',
    mobileBgFrame: 'https://dns82ajnfncm5.cloudfront.net/logos/EMAIL_MOBILE_BGFRAME.png',
    apexMechanicLogo: 'https://dns82ajnfncm5.cloudfront.net/logos/apex_mechanic.png',
};

const ApexAutoWrenchLogo = () => (
    <Box component="img" src={CLOUDFRONT_LOGOS.apexMechanicLogo} alt="Apex Auto Logo" sx={{ height: 70, width: 'auto', objectFit: 'contain' }} />
);

const AppStoreBadge = () => (
    <Box
        component="img"
        src={CLOUDFRONT_LOGOS.appBtn}
        alt="Download on the App Store"
        sx={{ height: 32, width: 'auto', cursor: 'pointer', objectFit: 'contain' }}
    />
);

const GooglePlayBadge = () => (
    <Box
        component="img"
        src={CLOUDFRONT_LOGOS.googleBtn}
        alt="Get it on Google Play"
        sx={{ height: 32, width: 'auto', cursor: 'pointer', objectFit: 'contain' }}
    />
);

const AppPhonesMockup = ({ shopMockupImage, width = '122px' }) => {
    const srcImage = shopMockupImage || CLOUDFRONT_LOGOS.mobileBgFrame;
    return <Box component="img" src={srcImage} alt="App Phone Mockup" sx={{ width, height: 'auto', objectFit: 'contain', display: 'block' }} />;
};

const EmailPreviewModal = ({ open, onClose, type, shopDetails, apexAutoAppLink, bookingSectionChecked = true }) => {
    const [showPromotion, setShowPromotion] = useState(bookingSectionChecked);

    useEffect(() => {
        if (open) {
            setShowPromotion(Boolean(bookingSectionChecked));
        }
    }, [open, bookingSectionChecked]);

    const saasDetails = (() => {
        try {
            return JSON.parse(localStorage.getItem('apex-saas_details')) || {};
        } catch (e) {
            return {};
        }
    })();

    const shopName = shopDetails?.shopName || shopDetails?.name || saasDetails?.shopName || saasDetails?.name || 'Vancouver Mall';
    const shopLogo = shopDetails?.shopLogo || 'https://dns82ajnfncm5.cloudfront.net/shopLogo/1751017179899.png';
    const shopAddressLine1 = shopDetails?.address || shopDetails?.shopAddressLine1 || 'Repair Shop Full Address';
    const shopAddressLine2 =
        [shopDetails?.city?.city || shopDetails?.cityName, shopDetails?.province?.province || shopDetails?.provinceName, shopDetails?.postalCode]
            .filter(Boolean)
            .join(', ') || 'Mississauga, Province Postal Code';
    const shopPhone = shopDetails?.formattedShopOwnerPhoneNumber || shopDetails?.shopPhone || shopDetails?.phone || '416-123-2780';

    const isWelcomeEmail = type === 'welcome_email';
    const dialogTitle = isWelcomeEmail ? 'Welcome to “Apex Auto App” Email Preview' : '\u201cBook with Apex Auto App\u201d Email Section';

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            PaperProps={{
                sx: {
                    width: '564px',
                    maxWidth: '100%',
                    borderRadius: '12px',
                    overflow: 'hidden',
                },
            }}
        >
            {/* Header: Height 64px, Padding 16px 24px */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    height: '64px',
                    px: '24px',
                    py: '16px',
                    borderBottom: '1px solid rgba(0, 0, 0, 0.10)',
                }}
            >
                <Typography
                    sx={{
                        color: 'rgba(0, 0, 0, 0.87)',
                        fontFeatureSettings: "'liga' off, 'clig' off",
                        fontFamily: 'Roboto',
                        fontSize: '20px',
                        fontStyle: 'normal',
                        fontWeight: 500,
                        lineHeight: '160%',
                        letterSpacing: '0.15px',
                    }}
                >
                    {dialogTitle}
                </Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon sx={{ color: '#000', fontSize: 24 }} />
                </IconButton>
            </Box>

            {/* Body: Padding 32px */}
            <DialogContent sx={{ p: '32px', bgcolor: '#F9FAFB', display: 'flex', justifyContent: 'center' }}>
                {isWelcomeEmail ? (
                    /* Welcome Email Template Card: Exact Fixed W: 500px, H: 628px */
                    <Box
                        sx={{
                            width: '500px',
                            height: '628px',
                            bgcolor: '#002699',
                            borderRadius: '8px',
                            p: '20px',
                            color: '#FFF',
                            position: 'relative',
                            boxSizing: 'border-box',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                        }}
                    >
                        {/* Header Row */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: '10px' }}>
                            <Box sx={{ flex: 1, pr: 1 }}>
                                <Box sx={{ mb: '10px' }}>
                                    <ApexAutoWrenchLogo />
                                </Box>

                                <Typography sx={{ fontSize: '22px', fontWeight: 700, lineHeight: '125%', mb: '6px' }}>Welcome to Apex Auto</Typography>
                                <Typography sx={{ fontSize: '10px', lineHeight: '140%', opacity: 0.9, mb: '4px' }}>Hello John,</Typography>

                                <Typography sx={{ fontSize: '10px', lineHeight: '140%', opacity: 0.9 }}>
                                    We&apos;re thrilled to welcome you to Apex Auto your go-to destination for a superior auto repair experience. Here&apos;s
                                    what you can look forward to:
                                </Typography>
                            </Box>

                            {/* Phone Mockup Illustration */}
                            <Box sx={{ width: '160px', flexShrink: 0 }}>
                                <AppPhonesMockup shopMockupImage={shopDetails?.shopMockupImageForEmail} width="160px" />
                            </Box>
                        </Box>

                        {/* 4 Feature Cards (2x2 Grid) */}
                        <Grid container spacing={1.2} sx={{ mb: '14px' }}>
                            <Grid item xs={6}>
                                <Box sx={{ bgcolor: '#FFF', color: '#1A1A1A', borderRadius: '8px', p: '12px', height: '100%' }}>
                                    <Box
                                        sx={{
                                            bgcolor: '#EBF3FB',
                                            color: '#006CDD',
                                            borderRadius: '6px',
                                            width: '26px',
                                            height: '26px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mb: '6px',
                                        }}
                                    >
                                        <CalendarTodayOutlinedIcon sx={{ fontSize: 16 }} />
                                    </Box>
                                    <Typography sx={{ fontSize: '12px', fontWeight: 700, mb: '2px' }}>Instant Appointments</Typography>
                                    <Typography sx={{ fontSize: '10px', color: '#555', lineHeight: '130%' }}>
                                        Book anytime, anywhere – no more phone calls needed.
                                    </Typography>
                                </Box>
                            </Grid>

                            <Grid item xs={6}>
                                <Box sx={{ bgcolor: '#FFF', color: '#1A1A1A', borderRadius: '8px', p: '12px', height: '100%' }}>
                                    <Box
                                        sx={{
                                            bgcolor: '#EBF3FB',
                                            color: '#006CDD',
                                            borderRadius: '6px',
                                            width: '26px',
                                            height: '26px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mb: '6px',
                                        }}
                                    >
                                        <ChatBubbleOutlineOutlinedIcon sx={{ fontSize: 16 }} />
                                    </Box>
                                    <Typography sx={{ fontSize: '12px', fontWeight: 700, mb: '2px' }}>Chat with your mechanic</Typography>
                                    <Typography sx={{ fontSize: '10px', color: '#555', lineHeight: '130%' }}>
                                        Have questions or want to stay informed? Connect with your mechanic in real time.
                                    </Typography>
                                </Box>
                            </Grid>

                            <Grid item xs={6}>
                                <Box sx={{ bgcolor: '#FFF', color: '#1A1A1A', borderRadius: '8px', p: '12px', height: '100%' }}>
                                    <Box
                                        sx={{
                                            bgcolor: '#EBF3FB',
                                            color: '#006CDD',
                                            borderRadius: '6px',
                                            width: '26px',
                                            height: '26px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mb: '6px',
                                        }}
                                    >
                                        <NotificationsNoneOutlinedIcon sx={{ fontSize: 16 }} />
                                    </Box>
                                    <Typography sx={{ fontSize: '12px', fontWeight: 700, mb: '2px' }}>Appointment Updates</Typography>
                                    <Typography sx={{ fontSize: '10px', color: '#555', lineHeight: '130%' }}>
                                        Stay in the loop with real-time notifications for your appointments – from booking to pick up.
                                    </Typography>
                                </Box>
                            </Grid>

                            <Grid item xs={6}>
                                <Box sx={{ bgcolor: '#FFF', color: '#1A1A1A', borderRadius: '8px', p: '12px', height: '100%' }}>
                                    <Box
                                        sx={{
                                            bgcolor: '#EBF3FB',
                                            color: '#006CDD',
                                            borderRadius: '6px',
                                            width: '26px',
                                            height: '26px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mb: '6px',
                                        }}
                                    >
                                        <DescriptionOutlinedIcon sx={{ fontSize: 16 }} />
                                    </Box>
                                    <Typography sx={{ fontSize: '12px', fontWeight: 700, mb: '2px' }}>Eco-Friendly Invoices</Typography>
                                    <Typography sx={{ fontSize: '10px', color: '#555', lineHeight: '130%' }}>
                                        Say goodbye to paper – electronic estimates and invoices are now at your fingertips.
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>

                        {/* Bottom Footer Area */}
                        <Box sx={{ mt: 'auto' }}>
                            <Typography sx={{ fontSize: '10px', lineHeight: '140%', opacity: 0.9, mb: '10px' }}>
                                Thank you for choosing Apex Auto for your auto repair needs. We can&apos;t wait to show you the difference. If you have any
                                questions or need assistance, don&apos;t hesitate to reach out.
                            </Typography>

                            <Button
                                variant="contained"
                                component={apexAutoAppLink ? 'a' : 'button'}
                                href={apexAutoAppLink || undefined}
                                target={apexAutoAppLink ? '_blank' : undefined}
                                rel={apexAutoAppLink ? 'noopener noreferrer' : undefined}
                                sx={{
                                    bgcolor: '#FFF',
                                    color: '#000000',
                                    fontWeight: 700,
                                    fontSize: '10px',
                                    borderRadius: '6px',
                                    px: '18px',
                                    py: '6px',
                                    textTransform: 'uppercase',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                    '&:hover': { bgcolor: '#F5F5F5', color: '#000000', boxShadow: '0 2px 6px rgba(0,0,0,0.15)' },
                                    mb: '10px',
                                }}
                            >
                                OPEN APEX AUTO
                            </Button>

                            <Typography sx={{ fontSize: '10px', lineHeight: '130%', opacity: 0.9 }}>
                                Kind Regards,
                                <br />
                                The Apex Auto Team
                            </Typography>
                        </Box>
                    </Box>
                ) : (
                    /* App Promotion In Emails Template Card */
                    <Box
                        sx={{
                            width: '500px',
                            minHeight: showPromotion ? '652px' : 'auto',
                            bgcolor: '#FFF',
                            border: '1px solid #E0E0E0',
                            borderRadius: '8px',
                            p: '24px',
                            boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
                            boxSizing: 'border-box',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            overflow: 'hidden',
                        }}
                    >
                        <Box>
                            {/* Dynamic Shop Header */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: '14px' }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                    <Box
                                        component="img"
                                        src={shopLogo}
                                        alt={shopName}
                                        sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: '50%', flexShrink: 0 }}
                                    />
                                    <Box sx={{ maxWidth: '280px' }}>
                                        <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#1A1A1A', lineHeight: '130%' }}>{shopName}</Typography>
                                        <Typography sx={{ fontSize: '10px', color: '#666', lineHeight: '135%' }}>{shopAddressLine1}</Typography>
                                        <Typography sx={{ fontSize: '10px', color: '#666', lineHeight: '135%' }}>{shopAddressLine2}</Typography>
                                        <Typography sx={{ fontSize: '10px', color: '#666', lineHeight: '135%' }}>{shopPhone}</Typography>
                                    </Box>
                                </Box>

                                <Box
                                    onClick={() => setShowPromotion((prev) => !prev)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        color: '#006CDD',
                                        cursor: 'pointer',
                                        flexShrink: 0,
                                        userSelect: 'none',
                                        '&:hover': { opacity: 0.85 },
                                    }}
                                >
                                    {showPromotion ? (
                                        <>
                                            <VisibilityOffOutlinedIcon sx={{ fontSize: 16 }} />
                                            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#006CDD', textTransform: 'uppercase' }}>
                                                HIDE
                                            </Typography>
                                        </>
                                    ) : (
                                        <>
                                            <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
                                            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#006CDD', textTransform: 'uppercase' }}>
                                                SHOW
                                            </Typography>
                                        </>
                                    )}
                                </Box>
                            </Box>

                            {/* Email Heading & Intro */}
                            <Typography sx={{ fontSize: '20px', fontWeight: 500, color: '#1A1A1A', mb: '8px' }}>Appointment Confirmed</Typography>

                            <Typography sx={{ fontSize: '11.5px', fontWeight: 400, color: '#333', mb: '6px' }}>Hello Momin Sheikh,</Typography>

                            <Typography sx={{ fontSize: '10px', fontWeight: 400, color: '#444', lineHeight: '145%', mb: '10px' }}>
                                Your appointment request has been accepted and your appointment is now confirmed. Track your vehicle status and manage your
                                appointment directly within the Apex Auto App.
                            </Typography>

                            <Divider sx={{ my: '10px', borderColor: '#E5E5E5' }} />

                            {/* Appointment Detail Items */}
                            <Stack spacing={1.2} sx={{ mb: '14px' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <Box
                                        component="img"
                                        src={CLOUDFRONT_LOGOS.emailDropOff}
                                        alt="Drop Off"
                                        sx={{ width: 34, height: 34, objectFit: 'contain', flexShrink: 0 }}
                                    />
                                    <Box>
                                        <Typography sx={{ fontSize: '10px', color: '#777' }}>Drop Off</Typography>
                                        <Typography sx={{ fontSize: '12.5px', fontWeight: 500, color: '#1A1A1A' }}>Jan 23, 2023 - 9:00 am</Typography>
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <Box
                                        component="img"
                                        src={CLOUDFRONT_LOGOS.vehicleIcon}
                                        alt="Vehicle Information"
                                        sx={{ width: 34, height: 34, objectFit: 'contain', flexShrink: 0 }}
                                    />
                                    <Box>
                                        <Typography sx={{ fontSize: '10px', color: '#777' }}>Vehicle Information</Typography>
                                        <Typography sx={{ fontSize: '12.5px', fontWeight: 500, color: '#1A1A1A' }}>2023 Audi Q5 Premium Plus</Typography>
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                    <Box
                                        component="img"
                                        src={CLOUDFRONT_LOGOS.serviceIcon}
                                        alt="Services"
                                        sx={{ width: 34, height: 34, objectFit: 'contain', flexShrink: 0, mt: '2px' }}
                                    />
                                    <Box>
                                        <Typography sx={{ fontSize: '10px', color: '#777' }}>Services</Typography>
                                        <Typography sx={{ fontSize: '12.5px', fontWeight: 500, color: '#1A1A1A' }}>Oil Change</Typography>
                                        <Typography sx={{ fontSize: '12.5px', fontWeight: 500, color: '#1A1A1A' }}>Tire Change</Typography>
                                    </Box>
                                </Box>
                            </Stack>

                            {/* "Book with Apex Auto App" Banner Section */}
                            {showPromotion && (
                                <Box
                                    sx={{
                                        backgroundImage: `url(${CLOUDFRONT_LOGOS.bgmFrame}), linear-gradient(135deg, #F0F6FF 0%, #DCEBFF 100%)`,
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        borderRadius: '14px',
                                        p: '15px 16px',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Box sx={{ flex: 1, pr: 1 }}>
                                        <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#1A1A1A', lineHeight: '120%', mb: '6px' }}>
                                            Book appointments
                                            <br />
                                            with the <span style={{ color: '#006CDD' }}>Apex Auto App</span>
                                        </Typography>

                                        <Typography sx={{ fontSize: '10px', color: '#444', lineHeight: '135%', mb: '12px' }}>
                                            Track your vehicle&apos;s status, chat with
                                            <br />
                                            {shopName} and much more
                                        </Typography>

                                        <Stack direction="row" spacing={0.8}>
                                            <AppStoreBadge />
                                            <GooglePlayBadge />
                                        </Stack>
                                    </Box>

                                    <Box sx={{ width: '150px', flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
                                        <AppPhonesMockup shopMockupImage={shopDetails?.shopMockupImageForEmail} width="150px" />
                                    </Box>
                                </Box>
                            )}
                        </Box>

                        {/* Footer */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', pt: '0px', mt: showPromotion ? '0px' : '16px' }}>
                            <Typography
                                sx={{
                                    fontSize: '10px',
                                    color: '#333',
                                    maxWidth: '346px',
                                }}
                            >
                                You are receiving this email because you have requested an appointment at{' '}
                                <span style={{ color: '#006CDD', fontWeight: 600 }}>{shopName}</span>, which partners with{' '}
                                <span style={{ color: '#006CDD', fontWeight: 600 }}>Apex Auto</span> for appointments.
                            </Typography>

                            {showPromotion && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
                                    <Typography sx={{ fontSize: '10.5px', color: '#777', lineHeight: 1 }}>Powered by</Typography>
                                    <Box
                                        component="img"
                                        src={CLOUDFRONT_LOGOS.apexAuto}
                                        alt="Apex Auto"
                                        sx={{ height: '20px', width: 'auto', objectFit: 'contain' }}
                                    />
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default EmailPreviewModal;
